import * as turfArea from '@turf/area';

/**
 * Calculate polygon area in acres using Turf.js for geodesic accuracy
 * Uses proper spherical area calculations that account for Earth's curvature
 * More accurate than simplified shoelace formulas, especially for larger areas
 * Matches the calculation method used in the backend (john-deere.service.ts)
 * 
 * @param geometry - GeoJSON Polygon geometry object
 * @returns Area in acres, rounded to 2 decimal places
 */
export const calculatePolygonArea = (geometry: any): number => {
  if (!geometry || geometry.type !== 'Polygon') return 0;
  
  try {
    // Use Turf.js for accurate geodesic area calculation (returns square meters)
    const polygon = {
      type: 'Feature' as const,
      geometry: geometry,
      properties: {}
    };
    
    const areaSqM = turfArea.default(polygon);
    
    // Convert from square meters to acres (1 acre = 4046.8564224 square meters)
    const acres = areaSqM / 4046.8564224;
    
    return Math.round(acres * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error('Error calculating polygon area:', error);
    return 0;
  }
};

/**
 * Calculate polygon area from a Feature or FeatureCollection-like object
 * Handles both { geometry: {...} } and direct geometry objects
 * 
 * @param polygonOrFeature - Either a GeoJSON geometry object or an object with a geometry property
 * @returns Area in acres, rounded to 2 decimal places
 */
export const calculatePolygonAreaFromFeature = (polygonOrFeature: any): number => {
  if (!polygonOrFeature) return 0;
  
  // If it has a geometry property, use that; otherwise assume it's a geometry object
  const geometry = polygonOrFeature.geometry || polygonOrFeature;
  return calculatePolygonArea(geometry);
};
